import ArrowLeft from "@/svg/ArrowLeft";
import ChatBubble from "@/svg/ChatBubble";
import CrcCheckboxCheck from "@/svg/CrcCheckboxCheck";
import Send from "@/svg/Send";
import { ChatMessage, TagFilterBar } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useRef, useState } from "react";
import type {
  ClinicalNoteResponse,
  NoteAssigneeResponse,
  NoteUserSnapshot,
} from "../../../api/clinicalTrialsApiTypes";
import ToolTipUsingPortal from "../../../components/toolTipUsingPortal/toolTipUsingPortal";
import {
  plainTextToRichText,
  richTextToPlainText,
} from "../../../utils/richText";
import "./PatientChat.scss";

interface PatientChatProps {
  note: ClinicalNoteResponse;
  assignees: NoteAssigneeResponse[];
  isSaving: boolean;
  onBack: () => void;
  onReply: (body: ReturnType<typeof plainTextToRichText>) => Promise<void>;
  onResolve: () => Promise<void>;
  onAddRecipients: (
    userIds: string[],
    expectedVersion?: number,
  ) => Promise<void>;
  onRemoveRecipient: (userId: string) => Promise<void>;
}

function initialRecipients(note: ClinicalNoteResponse): NoteUserSnapshot[] {
  const source = note.participants?.length
    ? note.participants
    : [note.creator, note.assignee].filter((user): user is NoteUserSnapshot =>
        Boolean(user),
      );
  return Array.from(
    new Map(source.map((recipient) => [recipient.user_id, recipient])).values(),
  );
}

function userLabel(user: ClinicalNoteResponse["creator"]): string {
  return user.display_name || user.email || "Unknown";
}

function initialsFromLabel(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase() || "?";
}

function formatChatTimestamp(timestamp: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function ReadReceiptAvatars({ readers }: { readers: NoteUserSnapshot[] }) {
  if (!readers.length) return null;
  const visibleReaders = readers.slice(0, 2);
  const remainingReaders = readers.length - visibleReaders.length;
  const hiddenReaderNames = readers.slice(2).map(userLabel).join(", ");
  const colors = ["bg-[#0057D9]", "bg-[#F36C00]"];

  return (
    <span className="absolute -bottom-2 right-2 z-10 flex items-center">
      {visibleReaders.map((reader, index) => (
        <span
          key={reader.user_id}
          className={`group relative ${index ? "-ml-1" : ""}`}
        >
          <span
            className={`flex h-4 w-4 items-center justify-center rounded-full border border-white text-[0.4375rem] font-semibold text-white ${colors[index]}`}
            tabIndex={0}
            aria-label={`Read by ${userLabel(reader)}`}
          >
            {initialsFromLabel(userLabel(reader))}
          </span>
          <span
            role="tooltip"
            className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-[0.625rem] font-medium text-white opacity-0 shadow-sm transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
          >
            {userLabel(reader)}
          </span>
        </span>
      ))}
      {remainingReaders > 0 ? (
        <span className="group relative -ml-1">
          <span
            className="flex h-4 w-4 items-center justify-center rounded-full border border-white bg-black text-[0.4375rem] font-semibold text-white"
            tabIndex={0}
            aria-label={`Read by ${hiddenReaderNames}`}
          >
            +{remainingReaders}
          </span>
          <span
            role="tooltip"
            className="pointer-events-none invisible absolute bottom-full right-0 z-50 mb-1 whitespace-nowrap rounded bg-black px-2 py-1 text-[0.625rem] font-medium text-white opacity-0 shadow-sm transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
          >
            {hiddenReaderNames}
          </span>
        </span>
      ) : null}
    </span>
  );
}

/** Display the selected note and its replies as a docked conversation. */
const PatientChat = ({
  note,
  assignees,
  isSaving,
  onBack,
  onReply,
  onResolve,
  onAddRecipients,
  onRemoveRecipient,
}: PatientChatProps) => {
  const canParticipate = note.current_user_is_participant === true;
  const [messageDraft, setMessageDraft] = useState("");
  const [showResolveTooltip, setShowResolveTooltip] = useState(false);
  const recipients = useMemo(() => initialRecipients(note), [note]);
  const removableRecipients = recipients.filter(
    (recipient) =>
      note.can_remove_participants === true &&
      recipient.user_id !== note.creator.user_id,
  );
  const protectedRecipients = recipients.filter(
    (recipient) =>
      !removableRecipients.some(
        (removable) => removable.user_id === recipient.user_id,
      ),
  );
  const [isAddingRecipients, setIsAddingRecipients] = useState(false);
  const [draftRecipients, setDraftRecipients] = useState<NoteUserSnapshot[]>(
    [],
  );
  const [recipientSearch, setRecipientSearch] = useState("");
  const resolveButtonRef = useRef<HTMLDivElement>(null);
  const recipientTags = useMemo(() => recipients.map(userLabel), [recipients]);
  const draftRecipientTags = useMemo(
    () => draftRecipients.map(userLabel),
    [draftRecipients],
  );
  const filteredAssignees = useMemo(() => {
    const search = recipientSearch.trim().toLowerCase();
    if (!search) return assignees;
    return assignees.filter((assignee) =>
      userLabel(assignee).toLowerCase().includes(search),
    );
  }, [assignees, recipientSearch]);
  const replies = note.activities.filter(
    (activity) =>
      activity.activity_type === "reply" &&
      activity.body &&
      !activity.deleted_at,
  );
  const readersForMessage = (createdAt: string): NoteUserSnapshot[] =>
    (note.read_receipts ?? [])
      .filter((receipt) => new Date(receipt.read_at) >= new Date(createdAt))
      .map((receipt) => receipt.user);

  const sendReply = async (): Promise<void> => {
    const trimmedMessage = messageDraft.trim();
    if (!trimmedMessage || isSaving) return;
    await onReply(plainTextToRichText(trimmedMessage));
    setMessageDraft("");
  };

  return (
    <div className="patient-chat--container relative flex h-full flex-col overflow-hidden">
      <div className="patient-header h-[3.75rem] p-3 bg-white border-b border-primaryGray-14 flex items-center gap-2">
        <button
          type="button"
          className="icon border-0 bg-transparent p-0 cursor-pointer"
          aria-label="Back to patient notes"
          onClick={onBack}
        >
          <ArrowLeft />
        </button>
        <div
          className="text min-w-0 truncate text-sm font-semibold"
          title={note.title}
        >
          {note.title}
        </div>
      </div>
      <div className="patient-content bg-white flex-1 overflow-auto p-4 flex flex-col gap-4 items-end justify-end">
        <div className="flex w-full justify-start">
          <div className="relative w-fit max-w-full">
            <ChatMessage
              senderName={userLabel(note.creator)}
              timestamp={formatChatTimestamp(note.created_at)}
              message={
                <>
                  {richTextToPlainText(note.body)}
                  <ReadReceiptAvatars
                    readers={readersForMessage(note.created_at)}
                  />
                </>
              }
              side="left"
              avatarLabel={initialsFromLabel(userLabel(note.creator))}
              maxWidth="36rem"
              bubbleClassName="relative"
            />
          </div>
        </div>
        {replies.map((activity) => {
          const sender = userLabel(activity.actor);
          return (
            <div
              key={activity.activity_id}
              className="flex w-full justify-start"
            >
              <div className="relative w-fit max-w-full">
                <ChatMessage
                  senderName={sender}
                  timestamp={formatChatTimestamp(activity.created_at)}
                  message={
                    <>
                      {richTextToPlainText(activity.body!)}
                      <ReadReceiptAvatars
                        readers={readersForMessage(activity.created_at)}
                      />
                    </>
                  }
                  side="left"
                  avatarLabel={initialsFromLabel(sender)}
                  maxWidth="36rem"
                  bubbleClassName="relative"
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="patient-chat-footer bg-white p-4 border-t border-primaryGray-14">
        <div className="flex min-w-0 items-center gap-2">
          {recipientTags.length ? (
            <div className="min-w-0 w-fit max-w-[calc(100%-8rem)] overflow-hidden">
              <div className="flex min-w-0 items-center gap-2">
                {protectedRecipients.length ? (
                  <TagFilterBar
                    tags={protectedRecipients.map(userLabel)}
                    closable={false}
                    showDropdown={false}
                    singleLineScrollable
                    className="w-fit max-w-full border-0 p-0"
                  />
                ) : null}
                {removableRecipients.length ? (
                  <TagFilterBar
                    tags={removableRecipients.map(userLabel)}
                    closable
                    showDropdown={false}
                    singleLineScrollable
                    className="w-fit max-w-full border-0 p-0"
                    onTagRemove={(tag) => {
                      const recipient = removableRecipients.find(
                        (candidate) => userLabel(candidate) === tag,
                      );
                      if (recipient && !isSaving) {
                        void onRemoveRecipient(recipient.user_id);
                      }
                    }}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
          {canParticipate ? (
            <button
              type="button"
              className="shrink-0 border-0 bg-transparent px-1 py-2 text-xs font-semibold text-tertiaryBlue-4"
              onClick={() => {
                setDraftRecipients(recipients);
                setRecipientSearch("");
                setIsAddingRecipients(true);
              }}
              disabled={isSaving || note.can_add_participants === false}
            >
              + Add Recipients
            </button>
          ) : null}
        </div>
        {canParticipate ? (
          <form
            className="flex items-center gap-2 mt-3 bg-white rounded border border-primaryGray-14 px-3"
            onSubmit={(event) => {
              event.preventDefault();
              void sendReply().catch(() => undefined);
            }}
          >
            <input
              type="text"
              value={messageDraft}
              onChange={(event) => setMessageDraft(event.target.value)}
              placeholder="Write a message..."
              aria-label="Reply to note"
              className="h-10 flex-1  text-[0.75rem] font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-2 outline-none placeholder:text-primaryGray-7"
            />
            <div className="actions flex gap-3">
              {note.status === "open" ? (
                <div
                  ref={resolveButtonRef}
                  className="icon-1 flex items-center"
                  onMouseEnter={() => setShowResolveTooltip(true)}
                  onMouseLeave={() => setShowResolveTooltip(false)}
                >
                  <button
                    type="button"
                    className="border-0 bg-transparent p-0 text-primaryGray-7 transition-all hover:scale-105 hover:text-tertiaryGreen-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-primaryGray-7"
                    aria-label="Mark As Resolved"
                    disabled={isSaving}
                    onFocus={() => setShowResolveTooltip(true)}
                    onBlur={() => setShowResolveTooltip(false)}
                    onClick={() => void onResolve().catch(() => undefined)}
                  >
                    <ChatBubble />
                  </button>
                  <ToolTipUsingPortal
                    placement="top-right"
                    showTooltip={showResolveTooltip}
                    parentRef={resolveButtonRef}
                  >
                    Mark As Resolved
                  </ToolTipUsingPortal>
                </div>
              ) : null}
              <button
                type="submit"
                className="icon-2 border-0 bg-transparent p-0 text-primaryGray-7 transition-all hover:scale-105 hover:text-primaryGray-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-primaryGray-7"
                aria-label="Send reply"
                disabled={!messageDraft.trim() || isSaving}
              >
                <Send />
              </button>
            </div>
          </form>
        ) : (
          <p className="mt-3 text-xs text-primaryGray-7">
            Only note participants can reply or update this note.
          </p>
        )}
      </div>
      {canParticipate && isAddingRecipients ? (
        <div
          className="absolute inset-0 z-50 flex items-end bg-black bg-opacity-40"
          onClick={() => setIsAddingRecipients(false)}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-note-recipients-title"
            className="flex max-h-[75%] w-full flex-col bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.16)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-primaryGray-14 px-6 py-5">
              <h2
                id="add-note-recipients-title"
                className="text-lg font-semibold text-primaryGray-1"
              >
                Add Recipients
              </h2>
            </div>

            <div className="flex min-h-0 flex-col gap-6 overflow-y-auto px-6 py-5">
              {draftRecipientTags.length ? (
                <TagFilterBar
                  tags={draftRecipientTags}
                  closable
                  showDropdown={false}
                  className="border-0 p-0"
                  onTagRemove={(tag) => {
                    setDraftRecipients((current) => {
                      const recipientIndex = current.findIndex(
                        (recipient) => userLabel(recipient) === tag,
                      );
                      if (
                        recipientIndex < 0 ||
                        current[recipientIndex].user_id === note.creator.user_id
                      ) {
                        return current;
                      }
                      return current.filter(
                        (_recipient, index) => index !== recipientIndex,
                      );
                    });
                  }}
                />
              ) : null}

              <div className="rounded-lg border border-primaryGray-14 p-4">
                <input
                  type="search"
                  value={recipientSearch}
                  onChange={(event) => setRecipientSearch(event.target.value)}
                  placeholder="Search for a recipient..."
                  aria-label="Search for a recipient"
                  className="h-11 w-full rounded border border-primaryGray-14 px-4 text-sm text-primaryGray-1 outline-none placeholder:text-primaryGray-7 focus:border-tertiaryBlue-4"
                />
                <div className="mt-3 flex max-h-72 flex-col overflow-y-auto">
                  {filteredAssignees.map((assignee) => {
                    const isSelected = draftRecipients.some(
                      (recipient) => recipient.user_id === assignee.user_id,
                    );
                    const isCreator = assignee.user_id === note.creator.user_id;
                    return (
                      <button
                        key={assignee.user_id}
                        type="button"
                        aria-pressed={isSelected}
                        disabled={isCreator}
                        className="flex items-center justify-between rounded bg-white px-2 py-3 text-left text-sm font-medium text-primaryGray-1 hover:bg-primaryGray-15"
                        onClick={() =>
                          setDraftRecipients((current) =>
                            isSelected
                              ? current.filter(
                                  (recipient) =>
                                    recipient.user_id !== assignee.user_id,
                                )
                              : [...current, assignee],
                          )
                        }
                      >
                        <span>{userLabel(assignee)}</span>
                        {isSelected ? (
                          <CrcCheckboxCheck strokeColor="#171717" />
                        ) : null}
                      </button>
                    );
                  })}
                  {!filteredAssignees.length ? (
                    <p className="px-1 py-3 text-sm text-primaryGray-7">
                      No recipients found
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 border-t border-primaryGray-14 px-6 py-5">
              <button
                type="button"
                className="h-11 rounded border border-primaryGray-1 bg-white text-sm font-semibold text-primaryGray-1"
                onClick={() => setIsAddingRecipients(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="h-11 rounded bg-primaryGray-1 text-sm font-semibold text-white"
                disabled={isSaving}
                onClick={async () => {
                  const currentIds = new Set(
                    recipients.map((recipient) => recipient.user_id),
                  );
                  const draftIds = new Set(
                    draftRecipients.map((recipient) => recipient.user_id),
                  );
                  const removedIds = recipients
                    .filter(
                      (recipient) =>
                        note.can_remove_participants === true &&
                        recipient.user_id !== note.creator.user_id &&
                        !draftIds.has(recipient.user_id),
                    )
                    .map((recipient) => recipient.user_id);
                  const addedIds = draftRecipients
                    .filter((recipient) => !currentIds.has(recipient.user_id))
                    .map((recipient) => recipient.user_id);
                  if (addedIds.length) {
                    await onAddRecipients(addedIds, note.version);
                  }
                  for (const userId of removedIds) {
                    await onRemoveRecipient(userId);
                  }
                  setIsAddingRecipients(false);
                }}
              >
                Add
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default PatientChat;
